import cx from 'clsx';
import React from 'react';

import { MEDIUM_BREAKPOINT } from '@/components/modular-grid/constants';
import SectionHeader from '@/components/SectionHeader';
import Spinner from '@/components/Spinner';
import { useWindowSize } from '@/hooks/useWindowSize';
import { CategoriesWithContentTypes } from '@/types/category/categoryTypes';
import { formatMessage } from '@/utils';
import { isEqual } from '@/utils/lodash-alternatives';

import styles from './Category.module.scss';
import CategoryCarousel from './CategoryCarousel';
import EmptyCategoryContent from './EmptyCategoryContent';
import { createSlideComponent } from './utils';

export const calculatePercentageWidth = (
  length: number,
  windowWidth: number,
  visiblePartLeft: number,
  visiblePartRight: number,
  edgePadding: number
) => {
  const tilePadding = 2 * edgePadding;
  const containerPadding =
    edgePadding + (visiblePartLeft + visiblePartRight) / 2;
  const spaceForThreeTiles = windowWidth - (3 * tilePadding + containerPadding);
  const possibleTileWidth = spaceForThreeTiles / 3;

  return [1, 2].includes(length)
    ? Math.round(
        ((possibleTileWidth + tilePadding) * length + containerPadding) * 100
      ) / windowWidth
    : 100;
};

export interface CategoryRendererWithSlidesPropTypes {
  category?: CategoriesWithContentTypes;
  locale: string;
  currency?: string;
  onGameTileClick?: (args: object) => void;
  onGameTileInfoClick?: (args: object) => void;
  onRouteTileClick?: (args: object) => void;
  onLinkTileClick?: (args: object) => void;
  onViewAllClick?: ({ path }: { path: string }) => void;
  onShuffleClick?: (args: object) => void;
  animation?: boolean;
  visible?: boolean;
  size?: 'small' | 'medium';
  pageTitle?: string;
  component_type?: string;
  type?: string;
}

const getCategoryTitle = (category: CategoriesWithContentTypes) => {
  if (
    category.component_type === 'game_grid' &&
    category.custom_widget_tile &&
    !Array.isArray(category.custom_widget_tile)
  )
    return '';
  if (category.component_type === 'jackpot_grid') return '';

  return category.title;
};

const isRecentlyPlayedGameGrid = (category: CategoriesWithContentTypes) =>
  category.id === 'recentlyPlayed' &&
  category.component_type === 'game_grid' &&
  category.type === 'Single Block';

const CategoryRenderer = React.memo(
  (props: CategoryRendererWithSlidesPropTypes) => {
    const {
      category,
      locale,
      currency,
      onGameTileClick,
      onGameTileInfoClick,
      onRouteTileClick,
      onLinkTileClick,
      onViewAllClick,
      onShuffleClick,
      animation,
      visible,
      size,
      pageTitle,
    } = props;

    const windowWidth = useWindowSize().width;

    /*
      Recently Played category is the only one fetched separately by the Data Gateway.
      This takes more time during which Spinner component should be displayed.
    */
    if (
      !category &&
      props.component_type === 'recently_played' &&
      props.type === 'Grid'
    ) {
      return (
        <div data-testid="category-placeholder" className={styles.placeholder}>
          <div className={styles.spinnerWrapper}>
            <Spinner animation={true} />
          </div>
        </div>
      );
    }
    if (!category || !visible || typeof category === 'string') return null;

    const {
      id,
      title,
      description,
      view_all_button,
      view_all_button_visible,
      slides = [],
      component_type,
      overlay,
      tableStartTime,
      autoplay,
      staticTexts = {
        closedTables: '',
        nextGame: '',
        comeBackLater: '',
        gameInfoTooltip: '',
        bingoTimer: {
          starting: '',
          closed: '',
        },
        emptyComponentText: '',
      },
      categoriesOnPage,
    } = category;
    if (!slides.length && !overlay) {
      return (
        <div
          data-testid="category-placeholder"
          className={styles.placeholder}
        ></div>
      );
    }

    const infoOverlay = {
      title: staticTexts.closedTables,
      description: tableStartTime
        ? formatMessage(staticTexts.nextGame, { startTime: tableStartTime })
        : staticTexts.comeBackLater,
    };

    const onCategoryClick = () =>
      view_all_button && onViewAllClick!({ path: view_all_button.route_link });
    const button = view_all_button_visible
      ? {
          text: view_all_button!.label,
          path: view_all_button!.route_link,
          notIndexed: view_all_button!.notIndexed,
          onClick: onCategoryClick,
        }
      : undefined;
    let percentageWidth = 100;

    if (
      category.component_type === 'game_grid' &&
      (category.type === 'Grid' || category.type === 'Single Block') &&
      category.slides
    ) {
      const lastSlide = category.slides[category.slides.length - 1];
      if (lastSlide && lastSlide.gameList) {
        const tilesInLastSlide = lastSlide.gameList.length;
        const expectedAmount = lastSlide.gamesLimit || 3;
        percentageWidth =
          tilesInLastSlide < expectedAmount
            ? calculatePercentageWidth(tilesInLastSlide, windowWidth, 20, 30, 7)
            : 100;
      }
    }

    const componentToRender = createSlideComponent({
      category,
      windowWidth,
      percentageWidth,
      onGameTileClick,
      onGameTileInfoClick,
      onRouteTileClick,
      onLinkTileClick,
      onShuffleClick,
      overlay: overlay ? infoOverlay : undefined,
      singleSlide:
        windowWidth >= MEDIUM_BREAKPOINT.min ||
        component_type === 'game_collection',
      size,
      locale,
      currency,
      pageTitle,
      gameInfoTooltip: staticTexts.gameInfoTooltip,
      staticTexts: { bingoTimer: staticTexts.bingoTimer },
    });

    const displayTitle = getCategoryTitle(category);

    let renderedBannerSlidesAmount = 0;

    const categoryHasSlides =
      category && category.slides && category.slides.length > 0;

    if (category.component_type === 'banner' && category.slides) {
      renderedBannerSlidesAmount = category.slides.filter(slide =>
        slide.banner && slide.banner.personalization
          ? slide.banner.personalization.rendered !== false
          : true
      ).length;
    }
    const shouldBeInfinite =
      component_type === 'banner' && windowWidth >= MEDIUM_BREAKPOINT.min;

    // Display animation only for Recently Played - Game Grid (Single Block)
    const shouldDisplayAppearanceAnimation = isRecentlyPlayedGameGrid(category);

    let preparedContent = null;
    if (componentToRender && categoryHasSlides) {
      preparedContent = (
        <CategoryCarousel
          {...{
            component_type,
            componentToRender,
            windowWidth,
            autoplay,
            renderedBannerSlidesAmount,
            percentageWidth,
          }}
        ></CategoryCarousel>
      );
    } else {
      if (categoriesOnPage !== 1) return null;

      // Show empty content only if there is single category on page GT-531
      preparedContent = (
        <EmptyCategoryContent
          emptyComponentText={staticTexts.emptyComponentText}
        ></EmptyCategoryContent>
      );
    }

    return (
      <div
        data-testid="category-item"
        data-test-title={title}
        key={id}
        className={cx(styles.wrapper, {
          [styles.wrapperAppearanceAnimation]: shouldDisplayAppearanceAnimation,
        })}
      >
        <div
          className={cx(styles.animationWrapper, {
            [styles.animation]: animation,
            [styles.infiniteWrapper]: shouldBeInfinite,
            [styles.subWrapperAppearanceAnimation]: shouldDisplayAppearanceAnimation,
            [styles.fadeIn]: shouldDisplayAppearanceAnimation,
          })}
        >
          {component_type !== 'banner' && (
            <SectionHeader
              heading={displayTitle}
              description={description}
              button={button}
            />
          )}
          {preparedContent}
        </div>
      </div>
    );
  },
  (props, nextprops) => isEqual(props, nextprops)
);

export default CategoryRenderer;
